import { FIGHTER, type FighterState, type MatchState } from "../sim";

/**
 * Вторичное движение (Джон 04.11.2026: «почему анимации выглядят так топорно»): тело отвечает на то, чего нет в клипе.
 *   приземление — колени гасят удар о пол, таз проседает и возвращается (клип в воздухе сам этого не знает);
 *   отдача у бьющего — удар встретил тело или блок, корпус и голова на миг отскакивают назад: вес удара без стоп-кадра.
 * Веса 0..1 считаются из состояния мира; память только о моменте приземления — её видно по смене «в воздухе → на полу».
 */
export interface SecondaryWeights {
  readonly land: number;
  readonly recoil: number;
}

/** Сколько кадров гаснет приземление и с какой скорости падения (px/кадр) — полная сила. */
const LAND_FRAMES = 16;
const LAND_DECAY = 4;
const LAND_FULL_SPEED = 6;
/** Отдача: сколько кадров после попадания и как быстро гаснет; в блок — сильнее, удар отскакивает. */
const RECOIL_FRAMES = 10;
const RECOIL_DECAY = 3;
const RECOIL_HIT = 0.6;

const GROUNDED = new Set<number>([
  FIGHTER.idle,
  FIGHTER.walkForward,
  FIGHTER.walkBack,
  FIGHTER.crouch,
  FIGHTER.block,
  FIGHTER.crouchBlock,
  FIGHTER.turn,
  FIGHTER.ability,
]);
const STRUCK = new Set<number>([FIGHTER.hitstun, FIGHTER.blockstun, FIGHTER.airHit]);

export class SecondaryMotion {
  private readonly wasAirborne = [false, false];
  private readonly lastFall = [0, 0];
  private readonly landed = [
    { frame: -LAND_FRAMES, speed: 0 },
    { frame: -LAND_FRAMES, speed: 0 },
  ];

  weights(state: MatchState, side: 0 | 1): SecondaryWeights {
    const fighter = state.fighters[side];
    const opponent = state.fighters[side === 0 ? 1 : 0];
    return { land: this.land(fighter, side, state.frame), recoil: recoil(fighter, opponent) };
  }

  private land(fighter: FighterState, side: 0 | 1, frame: number): number {
    const inAir = fighter.y < 0;
    const memory = this.landed[side];
    if (this.wasAirborne[side] && !inAir && memory)
      this.landed[side] = { frame, speed: Math.abs(this.lastFall[side] ?? 0) / 256 };
    this.wasAirborne[side] = inAir;
    this.lastFall[side] = fighter.vy;
    const landing = this.landed[side];
    if (!landing || inAir || !GROUNDED.has(fighter.state)) return 0;
    const since = frame - landing.frame;
    if (since < 0 || since > LAND_FRAMES) return 0;
    return Math.exp(-since / LAND_DECAY) * Math.min(1, landing.speed / LAND_FULL_SPEED);
  }
}

function recoil(fighter: FighterState, opponent: FighterState): number {
  if (fighter.state !== FIGHTER.ability || fighter.hasHit === 0) return 0;
  if (!STRUCK.has(opponent.state) || opponent.stateFrame > RECOIL_FRAMES) return 0;
  const strength = opponent.state === FIGHTER.blockstun ? 1 : RECOIL_HIT;
  return Math.exp(-opponent.stateFrame / RECOIL_DECAY) * strength;
}
